import { motion } from "framer-motion";
import { ExternalLink } from "lucide-react";

const LOGO_URL = "https://customer-assets.emergentagent.com/job_green-creations/artifacts/cf3sam7y_Untitled%20design.png";

const fadeUp = {
  hidden: { opacity: 0, y: 30 },
  visible: (i) => ({
    opacity: 1,
    y: 0,
    transition: { delay: i * 0.12, duration: 0.6, ease: "easeOut" },
  }),
};

export default function GalleryPage({
  testId,
  title,
  subtitle,
  items,
  videoSrc,
  videoCaption,
  imageFilter,
}) {
  return (
    <div data-testid={testId}>
      {/* Page Header */}
      <section className="py-20 md:py-28 bg-[#EAE5D9]" data-testid={`${testId}-header`}>
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="flex flex-col md:flex-row md:items-center md:justify-between gap-8"
          >
            <div className="flex-1">
              <p className="text-[#8B5E34] font-body text-sm tracking-widest uppercase mb-4">
                Collection
              </p>
              <h1 className="font-heading text-5xl sm:text-7xl tracking-tighter font-light text-[#222522]">
                {title}
              </h1>
              <p className="font-body text-base md:text-lg text-[#5C635B] mt-6 max-w-2xl leading-relaxed">
                {subtitle}
              </p>
            </div>
            <img
              src={LOGO_URL}
              alt="Small Feet"
              className="h-28 md:h-36 w-auto object-contain rounded flex-shrink-0"
              style={{ mixBlendMode: "multiply" }}
              data-testid={`${testId}-logo`}
            />
          </motion.div>

          {/* Shop Links */}
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6, delay: 0.2 }}
            className="flex gap-4 mt-8"
          >
            <a
              href="https://www.ebay.co.uk/usr/small_feet"
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[#3A5A3F] text-[#F9F8F5] text-sm font-body tracking-wider uppercase hover:bg-[#2d4832] transition-all hover:translate-y-[-1px] hover:shadow-lg"
              data-testid="shop-ebay-link"
            >
              Shop on eBay
              <ExternalLink size={14} />
            </a>
          </motion.div>
        </div>
      </section>

      {/* Video Section (if provided) */}
      {videoSrc && (
        <section className="py-16 md:py-20" data-testid={`${testId}-video-section`}>
          <div className="max-w-7xl mx-auto px-6 md:px-12">
            <div className="relative rounded overflow-hidden bg-[#EAE5D9]">
              <video
                src={videoSrc}
                autoPlay
                loop
                muted
                playsInline
                className="w-full object-contain"
                style={{ maxHeight: "500px" }}
                data-testid={`${testId}-video`}
              />
              {videoCaption && (
                <div className="absolute bottom-4 left-4 bg-[#131613]/80 px-4 py-2">
                  <p className="text-[#F9F8F5] text-xs font-body tracking-wider uppercase">
                    {videoCaption}
                  </p>
                </div>
              )}
            </div>
          </div>
        </section>
      )}

      {/* Gallery Grid */}
      <section
        className={`${videoSrc ? "pb-24 md:pb-32" : "py-24 md:py-32"}`}
        data-testid={`${testId}-gallery`}
      >
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 md:grid-cols-12 gap-6 md:gap-8">
            {items.map((item, i) => (
              <motion.div
                key={item.title}
                initial="hidden"
                whileInView="visible"
                viewport={{ once: true, margin: "-50px" }}
                custom={i}
                variants={fadeUp}
                className={`${item.span}`}
              >
                <div
                  className={`gallery-item ${item.height} relative rounded overflow-hidden ${item.contain ? (item.containBg || "bg-[#1e3a1e]") : ""}`}
                  data-testid={`gallery-item-${i}`}
                >
                  <img
                    src={item.image}
                    alt={item.title}
                    className={`w-full h-full ${item.contain ? "object-contain p-2" : "object-cover"} ${item.objectPos || ""} ${imageFilter || ""}`}
                    loading="lazy"
                  />
                  <div className="overlay">
                    <div className="w-full">
                      <h3 className="font-heading text-xl text-[#F9F8F5] mb-1">
                        {item.title}
                      </h3>
                      <p className="font-body text-sm text-[#F9F8F5]/70 mb-3">
                        {item.desc}
                      </p>
                      <div className="flex gap-3">
                        <a
                          href="https://www.ebay.co.uk/usr/small_feet"
                          target="_blank"
                          rel="noopener noreferrer"
                          className="inline-flex items-center gap-1 text-xs text-[#F9F8F5] font-body tracking-wider uppercase hover:text-[#8B5E34] transition-colors"
                          data-testid={`gallery-item-${i}-ebay`}
                        >
                          eBay <ExternalLink size={10} />
                        </a>
                      </div>
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
